import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import useResetPassword from '../../hooks/useResetPassword';
import SafeImage from '../../components/common/SafeImage';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        password: '',
        password_confirmation: ''
    });
    const [errors, setErrors] = useState({});
    const [error, setError] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Get token and phone_no from URL parameters
    const token = searchParams.get('token');
    const phone_no = searchParams.get('phone_no');

    // Use the custom hook for password reset
    const resetPasswordMutation = useResetPassword();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prevState => ({
                ...prevState,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        
        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Password confirmation is required';
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setErrors({});
        
        if (!validateForm()) {
            return;
        }

        if (!token || !phone_no) {
            setError('Invalid reset link. Please request a new password reset.');
            return;
        }

        resetPasswordMutation.mutate(
            {
                token,
                phone_no,
                password: formData.password,
                password_confirmation: formData.password_confirmation
            },
            {
                onSuccess: (response) => {
                    if (response.status === 'success') {
                        setIsSubmitted(true);
                        setError('');
                    }
                },
                onError: (error) => {
                    if (error.errors) {
                        setErrors(error.errors);
                    } else {
                        setError(error.message || 'Failed to reset password. Please try again.');
                    }
                }
            }
        );
    };

    // Check if we have valid token and phone_no
    useEffect(() => {
        if (!token || !phone_no) {
            setError('Invalid reset link. Please request a new password reset.');
        }
    }, [token, phone_no]);

    return (
        <div>
            {/* <!-- Breadcrumb Section Start --> */}
            {/* <section className="breadcrumb-section pt-0">
                <div className="container-fluid-lg">
                    <div className="row">
                        <div className="col-12">
                            <div className="breadcrumb-contain">
                                <h2>Reset Password</h2>
                                <nav>
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to="/">
                                                <i className="fa-solid fa-house"></i>
                                            </Link>
                                        </li>
                                        <li className="breadcrumb-item active">Reset Password</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}
            {/* <!-- Breadcrumb Section End --> */}

            {/* <!-- log in section start --> */}
            <section className="log-in-section section-b-space">
                <div className="container-fluid-lg w-100">
                    <div className="row">
                        <div className="col-xxl-6 col-xl-5 col-lg-6 d-lg-block d-none ms-auto">
                            <div className="image-contain">
                                <SafeImage src="../assets/images/inner-page/reset-password.png" className="img-fluid" alt="Reset password illustration" />
                            </div>
                        </div>

                        <div className="col-xxl-4 col-xl-5 col-lg-6 col-sm-8 mx-auto">
                            <div className="log-in-box">
                                <div className="log-in-title">
                                    <h3>Welcome To Go Home Express</h3>
                                    <h4>Reset Your Password</h4>
                                </div>

                                {error && (
                                    <div className="alert alert-danger" role="alert" aria-live="polite">
                                        {error}
                                    </div>
                                )}

                                <div className="input-box">
                                    {!isSubmitted ? (
                                        <form className="row g-4" onSubmit={handleSubmit} noValidate>
                                            <div className="col-12">
                                                <div className="form-floating theme-form-floating">
                                                    <input 
                                                        type="password" 
                                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                        id="password"
                                                        name="password"
                                                        placeholder="New Password"
                                                        value={formData.password}
                                                        onChange={handleChange}
                                                        autoComplete="new-password"
                                                        aria-describedby="passwordError"
                                                        aria-invalid={errors.password ? 'true' : 'false'}
                                                        required
                                                        disabled={resetPasswordMutation.isLoading}
                                                    />
                                                    <label htmlFor="password">New Password</label>
                                                    {errors.password && (
                                                        <div className="invalid-feedback" id="passwordError" role="alert">
                                                            {errors.password}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="form-floating theme-form-floating">
                                                    <input 
                                                        type="password" 
                                                        className={`form-control ${errors.password_confirmation ? 'is-invalid' : ''}`}
                                                        id="password_confirmation"
                                                        name="password_confirmation"
                                                        placeholder="Confirm New Password"
                                                        value={formData.password_confirmation}
                                                        onChange={handleChange}
                                                        autoComplete="new-password"
                                                        aria-describedby="passwordConfirmationError"
                                                        aria-invalid={errors.password_confirmation ? 'true' : 'false'}
                                                        required
                                                        disabled={resetPasswordMutation.isLoading}
                                                    />
                                                    <label htmlFor="password_confirmation">Confirm New Password</label>
                                                    {errors.password_confirmation && (
                                                        <div className="invalid-feedback" id="passwordConfirmationError" role="alert">
                                                            {errors.password_confirmation}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <button 
                                                    className="btn btn-animation w-100" 
                                                    type="submit"
                                                    disabled={resetPasswordMutation.isLoading || !token || !phone_no}
                                                    aria-busy={resetPasswordMutation.isLoading}
                                                >
                                                    {resetPasswordMutation.isLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Resetting Password...
                                                        </>
                                                    ) : 'Reset Password'}
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <div className="success-message text-center">
                                            <i className="fa-solid fa-check-circle mb-3" style={{ fontSize: '3rem', color: '#28a745' }}></i>
                                            <h4>Password Reset Successful</h4>
                                            <p className="text-muted">Your password has been reset successfully. You can now log in with your new password.</p>
                                            <button 
                                                className="btn btn-primary mt-3"
                                                onClick={() => navigate('/')}
                                            >
                                                Go to Login
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* <!-- log in section end --> */}
        </div>
    )
}

export default ResetPassword 